import { type ReactElement, useLayoutEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/dev/prototypes/trees")({
	component: TreesPrototype,
});

// --- Data ---

interface TreeItem {
	title: string;
	item_type: string;
	doc_type: string | null;
	added_at: string;
	children?: TreeItem[];
	generated?: "synthesis" | "computed";
}

const DATA: TreeItem[] = [
	{
		title: "Clearvoid as Agent Coordination Layer — Architecture Exploration",
		item_type: "document",
		doc_type: "idea",
		added_at: "2026-02-19T21:55:40.178509+00:00",
		children: [
			{
				title: "Clearvoid as Structured Thinking Tool",
				item_type: "document",
				doc_type: "idea",
				added_at: "2026-02-19T22:48:59.042352+00:00",
				children: [
					{
						title: "Clearvoid Architecture Iteration — Session Notes",
						item_type: "document",
						doc_type: "note",
						added_at: "2026-02-26T01:47:11.409655+00:00",
					},
					{
						title: "Reload — Product Concept",
						item_type: "document",
						doc_type: "idea",
						added_at: "2026-02-25T19:39:00.484455+00:00",
					},
					{
						title: "Clearvoid: Structured Thinking Tool Roadmap",
						item_type: "document",
						doc_type: "idea",
						added_at: "2026-02-25T05:56:26.842003+00:00",
						children: [
							{
								title: "Phase 3: Questions as Agent Tasks",
								item_type: "document",
								doc_type: "note",
								added_at: "2026-02-25T05:55:14.560319+00:00",
							},
							{
								title: "Phase 2: Contribution Mechanic",
								item_type: "document",
								doc_type: "note",
								added_at: "2026-02-25T05:55:02.973267+00:00",
							},
						],
					},
					{
						title: "Agent-First Interface — Exploration",
						item_type: "document",
						doc_type: "exploration",
						added_at: "2026-02-22T17:21:35.165775+00:00",
					},
					{
						title: "Structured Thinking Tool — Competitive Landscape (Web Research)",
						item_type: "document",
						doc_type: "research",
						added_at: "2026-02-22T15:49:09.560657+00:00",
					},
					{
						title: "Structured Thinking Tool — Repo Research Synthesis",
						item_type: "document",
						doc_type: "synthesis",
						added_at: "2026-02-22T14:48:34.559067+00:00",
						children: [
							{
								title: "Ars Contexta — Repo Research Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-25T21:55:38.347973+00:00",
							},
							{
								title: "Supermemory — Repo Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-22T14:47:40.539749+00:00",
							},
							{
								title: "Ironclaw (OpenClaw) — Repo Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-22T14:47:14.27845+00:00",
							},
							{
								title: "Rowboat — Repo Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-22T14:46:47.07807+00:00",
							},
							{
								title: "Spacebot (Spacedrive) — Repo Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-22T14:46:18.66909+00:00",
							},
						],
					},
					{
						title: "Top-Down vs. Bottom-Up Strategy",
						item_type: "document",
						doc_type: "research",
						added_at: "2026-02-19T23:19:10.848919+00:00",
					},
					{
						title: "Defensibility of the Structured Thinking Tool — Exploration",
						item_type: "document",
						doc_type: "exploration",
						added_at: "2026-02-19T23:16:16.806275+00:00",
					},
				],
			},
			{
				title: "Supermemory.ai — Competitive Research",
				item_type: "document",
				doc_type: "research",
				added_at: "2026-02-19T22:48:31.116982+00:00",
			},
		],
	},
	{
		title: "I Made Claude Code and Codex Build the Same App (RAW RESULTS)",
		item_type: "youtube_video",
		doc_type: null,
		added_at: "2026-02-19T17:17:56.702+00:00",
	},
	{
		title: "Document workflow with MCP servers | Claude",
		item_type: "ai_thread",
		doc_type: null,
		added_at: "2026-02-19T17:16:51.304+00:00",
	},
	{
		title: "How Will People Generate Wealth If AI Does Everything?",
		item_type: "youtube_video",
		doc_type: null,
		added_at: "2026-02-19T17:15:35.945+00:00",
	},
	{
		title: "We need to talk about Sonnet 4.6",
		item_type: "youtube_video",
		doc_type: null,
		added_at: "2026-02-19T14:50:42.799+00:00",
	},
];

// --- Helpers ---

const TYPE_LABELS: Record<string, string> = {
	youtube_video: "YouTube",
	ai_thread: "AI Thread",
	website: "Website",
	url: "URL",
	document: "Document",
	upload: "Upload",
};

function typeLabel(item: TreeItem): string {
	if (item.item_type === "document" && item.doc_type) {
		return item.doc_type.charAt(0).toUpperCase() + item.doc_type.slice(1);
	}
	return TYPE_LABELS[item.item_type] ?? item.item_type;
}

function relativeDate(iso: string): string {
	const d = new Date(iso);
	const now = new Date();
	const days = Math.floor(
		(now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24),
	);
	if (days === 0) return "Today";
	if (days === 1) return "Yesterday";
	if (days < 7) return `${days}d ago`;
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function countDescendants(item: TreeItem): number {
	if (!item.children) return 0;
	return item.children.reduce((n, c) => n + 1 + countDescendants(c), 0);
}

// =============================================================================
// VARIANT A — Indented list with vertical connector lines
// =============================================================================

function VariantANode({
	item,
	depth,
}: {
	item: TreeItem;
	depth: number;
}): ReactElement {
	const [open, setOpen] = useState(true);
	const has = item.children && item.children.length > 0;

	return (
		<div className="relative">
			<div
				className="group flex cursor-default items-baseline gap-3 py-2 pr-4"
				style={{ paddingLeft: depth * 24 + 16 }}
			>
				{/* Connector dot */}
				<span
					className={`relative z-10 mt-px flex h-2 w-2 shrink-0 items-center justify-center rounded-full ${
						has
							? "bg-accent"
							: "border-line-strong bg-surface border"
					}`}
				/>

				{/* Title */}
				<span className="text-fg-secondary min-w-0 truncate text-sm">
					{item.title}
				</span>

				{/* Badge */}
				<span className="bg-surface-raised text-fg-ghost shrink-0 rounded px-1.5 py-0.5 text-[10px] tracking-wider uppercase">
					{typeLabel(item)}
				</span>

				{/* Date */}
				<span className="text-fg-faint shrink-0 text-[11px]">
					{relativeDate(item.added_at)}
				</span>

				{/* Expand toggle */}
				{has && (
					<button
						type="button"
						onClick={() => setOpen(!open)}
						className="text-fg-ghost hover:text-fg-muted ml-auto shrink-0 text-xs"
					>
						{open ? "collapse" : "expand"}
					</button>
				)}
			</div>

			{has && open && (
				<div className="relative">
					{/* Vertical line */}
					<div
						className="border-line absolute top-0 bottom-2 border-l"
						style={{ left: depth * 24 + 20 }}
					/>
					{item.children!.map((child, i) => (
						<VariantANode key={i} item={child} depth={depth + 1} />
					))}
				</div>
			)}
		</div>
	);
}

function VariantA(): ReactElement {
	return (
		<div>
			<h2 className="text-fg-strong mb-1 text-lg font-semibold">
				A — Indented List
			</h2>
			<p className="text-fg-muted mb-4 text-sm">
				Classic file-tree style with connector lines and
				expand/collapse.
			</p>
			<div className="border-line rounded-lg border">
				{DATA.map((item, i) => (
					<VariantANode key={i} item={item} depth={0} />
				))}
			</div>
		</div>
	);
}

// =============================================================================
// VARIANT B — Nested cards (outline style)
// =============================================================================

function VariantBNode({
	item,
	depth,
}: {
	item: TreeItem;
	depth: number;
}): ReactElement {
	const [open, setOpen] = useState(true);
	const has = item.children && item.children.length > 0;
	const count = countDescendants(item);

	const DEPTH_BG = [
		"bg-surface",
		"bg-surface-raised",
		"bg-skeleton",
		"bg-surface-hover",
	];
	const bg = DEPTH_BG[Math.min(depth, DEPTH_BG.length - 1)];

	return (
		<div
			className={`${
				depth === 0 ? "border-line border" : "border-line-subtle border"
			} rounded-lg ${bg}`}
		>
			<div
				className="flex items-center gap-3 px-4 py-3"
				role={has ? "button" : undefined}
				onClick={has ? () => setOpen(!open) : undefined}
			>
				{/* Chevron */}
				{has && (
					<svg
						className={`text-fg-ghost h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
						viewBox="0 0 16 16"
						fill="currentColor"
					>
						<path d="M6 3l5 5-5 5V3z" />
					</svg>
				)}

				<div className="flex min-w-0 flex-1 items-baseline gap-2">
					<span
						className={`min-w-0 truncate text-sm ${depth === 0 ? "text-fg-strong font-medium" : "text-fg-secondary"}`}
					>
						{item.title}
					</span>
					<span className="text-fg-ghost text-[10px] tracking-wider uppercase">
						{typeLabel(item)}
					</span>
				</div>

				<div className="flex shrink-0 items-center gap-3">
					{has && (
						<span className="text-fg-faint text-[11px]">
							{count} item{count > 1 ? "s" : ""}
						</span>
					)}
					<span className="text-fg-ghost text-[11px]">
						{relativeDate(item.added_at)}
					</span>
				</div>
			</div>

			{has && open && (
				<div className="space-y-2 px-4 pt-1 pb-4">
					{item.children!.map((child, i) => (
						<VariantBNode key={i} item={child} depth={depth + 1} />
					))}
				</div>
			)}
		</div>
	);
}

function VariantB(): ReactElement {
	return (
		<div>
			<h2 className="text-fg-strong mb-1 text-lg font-semibold">
				B — Nested Cards
			</h2>
			<p className="text-fg-muted mb-4 text-sm">
				Cards within cards. Each depth level gets a subtler surface.
			</p>
			<div className="space-y-3">
				{DATA.map((item, i) => (
					<VariantBNode key={i} item={item} depth={0} />
				))}
			</div>
		</div>
	);
}

// =============================================================================
// VARIANT C — Horizontal tree with connector lines
// =============================================================================

const BRIDGE_W = 40; // horizontal gap between card and children (px)
const CARD_W = 240; // card width (px)

function HCard({
	item,
	expanded,
	onToggle,
}: {
	item: TreeItem;
	expanded: boolean;
	onToggle: (() => void) | null;
}): ReactElement {
	const has = !!item.children?.length;
	const count = countDescendants(item);

	return (
		<div
			className="border-line bg-surface relative rounded-lg border shadow-sm"
			style={{ width: CARD_W }}
		>
			{/* Type badge — top right */}
			<span className="bg-accent-wash text-accent absolute -top-2.5 right-3 rounded-full px-2 py-0.5 text-[9px] font-medium tracking-wider uppercase">
				{typeLabel(item)}
			</span>

			<div className="px-3.5 pt-4 pb-3">
				<p className="text-fg-strong line-clamp-2 text-[13px] leading-snug font-medium">
					{item.title}
				</p>
				<div className="text-fg-ghost mt-2 flex items-center gap-2 text-[10px]">
					<span>{relativeDate(item.added_at)}</span>
					{has && (
						<>
							<span className="text-line-strong">|</span>
							<span>
								{count} item{count > 1 ? "s" : ""}
							</span>
						</>
					)}
				</div>
			</div>

			{/* Expand/collapse toggle */}
			{has && onToggle && (
				<button
					type="button"
					onClick={onToggle}
					className="border-line text-fg-ghost hover:text-fg-muted hover:bg-surface-raised bg-surface absolute top-1/2 -right-3 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border text-xs transition-colors"
				>
					{expanded ? "\u2212" : "+"}
				</button>
			)}
		</div>
	);
}

function HTreeNode({ item }: { item: TreeItem }): ReactElement {
	const [expanded, setExpanded] = useState(true);
	const childColRef = useRef<HTMLDivElement>(null);
	const [spine, setSpine] = useState<{
		top: number;
		bottom: number;
	} | null>(null);
	const has = item.children && item.children.length > 0;
	const childCount = item.children?.length ?? 0;
	const needsSpine = childCount > 1;

	useLayoutEffect(() => {
		if (!expanded || !needsSpine || !childColRef.current) {
			setSpine(null);
			return;
		}
		const el = childColRef.current;
		const rows = el.querySelectorAll<HTMLElement>(":scope > [data-row]");
		if (rows.length < 2) {
			setSpine(null);
			return;
		}
		const rect = el.getBoundingClientRect();
		const first = rows[0].getBoundingClientRect();
		const last = rows[rows.length - 1].getBoundingClientRect();
		setSpine({
			top: first.top + first.height / 2 - rect.top,
			bottom: rect.bottom - (last.top + last.height / 2),
		});
	}, [expanded, needsSpine, childCount]);

	return (
		<div className="flex items-center">
			{/* Card */}
			<div className="shrink-0">
				<HCard
					item={item}
					expanded={expanded}
					onToggle={has ? () => setExpanded(!expanded) : null}
				/>
			</div>

			{has && expanded && (
				<>
					{/* Horizontal bridge from parent to children */}
					<div
						className="bg-line-strong h-px shrink-0"
						style={{ width: BRIDGE_W }}
					/>

					{/* Children column — pl-3 creates room for spine + ticks */}
					<div
						ref={childColRef}
						className="relative flex shrink-0 flex-col gap-3 pl-3"
					>
						{/* Vertical spine (measured via ref) */}
						{spine && (
							<div
								className="border-line-strong absolute border-l"
								style={{
									left: 0,
									top: spine.top,
									bottom: spine.bottom,
								}}
							/>
						)}

						{item.children!.map((child, i) => (
							<div
								key={i}
								data-row
								className="relative flex items-center"
							>
								{/* Horizontal tick from spine to child */}
								<div
									className="bg-line-strong absolute h-px"
									style={{ left: -12, width: 12 }}
								/>
								<HTreeNode item={child} />
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
}

function VariantC(): ReactElement {
	return (
		<div>
			<h2 className="text-fg-strong mb-1 text-lg font-semibold">
				C — Horizontal Tree
			</h2>
			<p className="text-fg-muted mb-4 text-sm">
				Left-to-right flow with card nodes and connector lines.
				Horizontally scrollable.
			</p>
			<div className="border-line overflow-x-auto rounded-xl border p-8">
				<div className="flex w-max flex-col gap-6">
					{DATA.map((item, i) => (
						<HTreeNode key={i} item={item} />
					))}
				</div>
			</div>
		</div>
	);
}

// =============================================================================
// VARIANT D — Indented cards with connector lines
// =============================================================================

const INDENT = 32; // px per depth level
const LINE_X_OFFSET = 12; // px from left padding edge to connector center

function VariantDNode({
	item,
	depth,
	isLast,
}: {
	item: TreeItem;
	depth: number;
	isLast: boolean;
}): ReactElement {
	const [open, setOpen] = useState(true);
	const has = item.children && item.children.length > 0;
	const count = countDescendants(item);
	const left = depth * INDENT;

	return (
		<div className="relative">
			{/* Vertical line from parent down to this node */}
			{depth > 0 && (
				<div
					className="border-line-emphasis absolute top-0 border-l"
					style={{
						left: left - INDENT + LINE_X_OFFSET,
						height: isLast ? 24 : "100%",
					}}
				/>
			)}

			{/* Horizontal tick into this card */}
			{depth > 0 && (
				<div
					className="bg-line-emphasis absolute top-6 h-px"
					style={{
						left: left - INDENT + LINE_X_OFFSET,
						width: INDENT - LINE_X_OFFSET,
					}}
				/>
			)}

			{/* Card */}
			<div className="relative py-1.5" style={{ paddingLeft: left }}>
				<div className="border-line bg-surface hover:border-line-strong group relative rounded-lg border px-3.5 py-2.5 shadow-sm transition-colors">
					<div className="flex items-center gap-2">
						{/* Chevron */}
						{has && (
							<button
								type="button"
								onClick={() => setOpen(!open)}
								className="text-fg-ghost hover:text-fg-muted -ml-0.5 flex shrink-0 items-center justify-center"
							>
								<svg
									className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`}
									viewBox="0 0 16 16"
									fill="currentColor"
								>
									<path d="M6 3l5 5-5 5V3z" />
								</svg>
							</button>
						)}

						<div className="flex min-w-0 flex-1 items-baseline gap-2">
							<span className="text-fg-secondary min-w-0 truncate text-sm">
								{item.title}
							</span>
							<span className="bg-accent-wash text-accent shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium tracking-wider uppercase">
								{typeLabel(item)}
							</span>
						</div>

						<div className="flex shrink-0 items-center gap-3">
							{has && (
								<span className="text-fg-faint text-[11px]">
									{count}
								</span>
							)}
							<span className="text-fg-ghost text-[11px]">
								{relativeDate(item.added_at)}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Children */}
			{has &&
				open &&
				item.children!.map((child, i) => (
					<VariantDNode
						key={i}
						item={child}
						depth={depth + 1}
						isLast={i === item.children!.length - 1}
					/>
				))}
		</div>
	);
}

function VariantD(): ReactElement {
	return (
		<div>
			<h2 className="text-fg-strong mb-1 text-lg font-semibold">
				D — Indented Cards
			</h2>
			<p className="text-fg-muted mb-4 text-sm">
				Each node is its own card, indented with connector lines like a
				file tree.
			</p>
			<div className="space-y-0">
				{DATA.map((item, i) => (
					<VariantDNode
						key={i}
						item={item}
						depth={0}
						isLast={i === DATA.length - 1}
					/>
				))}
			</div>
		</div>
	);
}

// =============================================================================
// VARIANT E — Flat cards with synthesis + computed nodes
// =============================================================================

// Modified data: "Structured Thinking Tool" branch gets a synthesis node
// that replaces the branch root, plus a computed child.
const DATA_E: TreeItem[] = [
	{
		title: "Clearvoid as Agent Coordination Layer — Architecture Exploration",
		item_type: "document",
		doc_type: "idea",
		added_at: "2026-02-19T21:55:40.178509+00:00",
		children: [
			{
				title: "Clearvoid as Structured Thinking Tool — Synthesis",
				item_type: "document",
				doc_type: "synthesis",
				added_at: "2026-02-26T04:00:00.000+00:00",
				generated: "synthesis",
				children: [
					{
						title: "All GitHub Repositories Referenced in This Branch",
						item_type: "document",
						doc_type: "extraction",
						added_at: "2026-02-26T04:15:00.000+00:00",
						generated: "computed",
					},
					{
						title: "Clearvoid Architecture Iteration — Session Notes",
						item_type: "document",
						doc_type: "note",
						added_at: "2026-02-26T01:47:11.409655+00:00",
					},
					{
						title: "Reload — Product Concept",
						item_type: "document",
						doc_type: "idea",
						added_at: "2026-02-25T19:39:00.484455+00:00",
					},
					{
						title: "Clearvoid: Structured Thinking Tool Roadmap",
						item_type: "document",
						doc_type: "idea",
						added_at: "2026-02-25T05:56:26.842003+00:00",
						children: [
							{
								title: "Phase 3: Questions as Agent Tasks",
								item_type: "document",
								doc_type: "note",
								added_at: "2026-02-25T05:55:14.560319+00:00",
							},
							{
								title: "Phase 2: Contribution Mechanic",
								item_type: "document",
								doc_type: "note",
								added_at: "2026-02-25T05:55:02.973267+00:00",
							},
						],
					},
					{
						title: "Agent-First Interface — Exploration",
						item_type: "document",
						doc_type: "exploration",
						added_at: "2026-02-22T17:21:35.165775+00:00",
					},
					{
						title: "Structured Thinking Tool — Repo Research Synthesis",
						item_type: "document",
						doc_type: "synthesis",
						added_at: "2026-02-22T14:48:34.559067+00:00",
						children: [
							{
								title: "Supermemory — Repo Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-22T14:47:40.539749+00:00",
							},
							{
								title: "Ironclaw (OpenClaw) — Repo Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-22T14:47:14.27845+00:00",
							},
							{
								title: "Rowboat — Repo Analysis",
								item_type: "document",
								doc_type: "research",
								added_at: "2026-02-22T14:46:47.07807+00:00",
							},
						],
					},
					{
						title: "Defensibility of the Structured Thinking Tool — Exploration",
						item_type: "document",
						doc_type: "exploration",
						added_at: "2026-02-19T23:16:16.806275+00:00",
					},
				],
			},
			{
				title: "Supermemory.ai — Competitive Research",
				item_type: "document",
				doc_type: "research",
				added_at: "2026-02-19T22:48:31.116982+00:00",
			},
		],
	},
	{
		title: "I Made Claude Code and Codex Build the Same App (RAW RESULTS)",
		item_type: "youtube_video",
		doc_type: null,
		added_at: "2026-02-19T17:17:56.702+00:00",
	},
	{
		title: "Document workflow with MCP servers | Claude",
		item_type: "ai_thread",
		doc_type: null,
		added_at: "2026-02-19T17:16:51.304+00:00",
	},
	{
		title: "How Will People Generate Wealth If AI Does Everything?",
		item_type: "youtube_video",
		doc_type: null,
		added_at: "2026-02-19T17:15:35.945+00:00",
	},
	{
		title: "We need to talk about Sonnet 4.6",
		item_type: "youtube_video",
		doc_type: null,
		added_at: "2026-02-19T14:50:42.799+00:00",
	},
];

const E_INDENT = 32;
const E_LINE_X = 12;

function VariantENode({
	item,
	depth,
	isLast,
}: {
	item: TreeItem;
	depth: number;
	isLast: boolean;
}): ReactElement {
	const [open, setOpen] = useState(true);
	const has = item.children && item.children.length > 0;
	const count = countDescendants(item);
	const left = depth * E_INDENT;

	const isSynthesis = item.generated === "synthesis";
	const isComputed = item.generated === "computed";
	const isGenerated = isSynthesis || isComputed;

	// Card style based on node type
	let cardClasses = "border px-3.5 py-2.5 transition-colors ";
	if (isSynthesis) {
		cardClasses += "bg-fg-strong border-fg-strong text-on-accent";
	} else if (isComputed) {
		cardClasses += "bg-accent border-accent text-on-accent";
	} else {
		cardClasses +=
			"bg-surface border-line hover:border-line-strong shadow-sm";
	}

	// Badge color based on node type
	let badgeClasses =
		"shrink-0 px-1.5 py-0.5 text-[9px] leading-none font-medium tracking-wider uppercase ";
	if (isSynthesis) {
		badgeClasses += "bg-on-accent text-fg-strong";
	} else if (isComputed) {
		badgeClasses += "bg-on-accent text-accent";
	} else {
		badgeClasses += "bg-accent-wash text-accent";
	}

	return (
		<div className="relative">
			{/* Vertical line from parent down to this node */}
			{depth > 0 && (
				<div
					className="border-line-emphasis absolute top-0 border-l"
					style={{
						left: left - E_INDENT + E_LINE_X,
						height: isLast ? 24 : "100%",
					}}
				/>
			)}

			{/* Horizontal tick into this card */}
			{depth > 0 && (
				<div
					className="bg-line-emphasis absolute top-6 h-px"
					style={{
						left: left - E_INDENT + E_LINE_X,
						width: E_INDENT - E_LINE_X,
					}}
				/>
			)}

			{/* Card */}
			<div className="relative py-1.5" style={{ paddingLeft: left }}>
				<div className={cardClasses}>
					<div className="flex items-center gap-2">
						{/* Chevron */}
						{has && (
							<button
								type="button"
								onClick={() => setOpen(!open)}
								className={`-ml-0.5 flex shrink-0 items-center justify-center ${
									isGenerated
										? "text-on-accent opacity-50 hover:opacity-80"
										: "text-fg-ghost hover:text-fg-muted"
								}`}
							>
								<svg
									className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`}
									viewBox="0 0 16 16"
									fill="currentColor"
								>
									<path d="M6 3l5 5-5 5V3z" />
								</svg>
							</button>
						)}

						<div className="flex min-w-0 flex-1 items-center gap-2">
							<span
								className={`min-w-0 truncate text-sm ${
									isGenerated
										? "font-medium"
										: "text-fg-secondary"
								}`}
							>
								{item.title}
							</span>
							<span className={badgeClasses}>
								{isGenerated ? item.generated : typeLabel(item)}
							</span>
						</div>

						<div className="flex shrink-0 items-center gap-3">
							{has && (
								<span
									className={`text-[11px] ${isGenerated ? "opacity-50" : "text-fg-faint"}`}
								>
									{count}
								</span>
							)}
							<span
								className={`text-[11px] ${isGenerated ? "opacity-40" : "text-fg-ghost"}`}
							>
								{relativeDate(item.added_at)}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Children */}
			{has &&
				open &&
				item.children!.map((child, i) => (
					<VariantENode
						key={i}
						item={child}
						depth={depth + 1}
						isLast={i === item.children!.length - 1}
					/>
				))}
		</div>
	);
}

function VariantE(): ReactElement {
	return (
		<div>
			<h2 className="text-fg-strong mb-1 text-lg font-semibold">
				E — Indented Cards with Generated Nodes
			</h2>
			<p className="text-fg-muted mb-4 text-sm">
				Flat cards like D, but with synthesis and computed nodes
				injected. No rounded corners. Generated nodes use bold
				backgrounds.
			</p>
			<div className="space-y-0">
				{DATA_E.map((item, i) => (
					<VariantENode
						key={i}
						item={item}
						depth={0}
						isLast={i === DATA_E.length - 1}
					/>
				))}
			</div>
		</div>
	);
}

// =============================================================================
// Page
// =============================================================================

function TreesPrototype(): ReactElement {
	return (
		<div className="mx-auto max-w-5xl space-y-16 px-6 py-12 pb-32">
			<div>
				<h1 className="text-fg-strong mb-1 text-2xl font-semibold">
					Tree View Prototypes
				</h1>
				<p className="text-fg-muted text-sm">
					Five approaches to rendering a nested library tree.
				</p>
			</div>

			<VariantA />
			<VariantB />
			<VariantC />
			<VariantD />
			<VariantE />
		</div>
	);
}
